import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useSession } from '../../stores/session';

/**
 * `/join/{username}` - somebody's referral link.
 *
 * ## It remembers, then gets out of the way
 *
 * The link's whole job is to say who sent somebody here and then show them the
 * sign-up form. It stores the handle and redirects; there is no page to look
 * at, because a page saying "you were invited, click here" is a step between
 * somebody and the thing they came to do.
 *
 * ## Stored rather than only carried in the URL
 *
 * People do not sign up the moment they arrive. They read the front page, play
 * something, come back tomorrow - and a referral held only in a query string is
 * lost the first time they click anything. It survives in `localStorage` until
 * they register, which is the only moment it is read.
 *
 * ## Already signed in is not a referral
 *
 * Somebody with an account who opens a friend's link goes to that friend's
 * profile, which is what they were being sent to in spirit. Storing a referral
 * for an account that already exists would be storing something that can never
 * be claimed.
 */
export function JoinPage() {
  const { username } = useParams<{ username: string }>();
  const user = useSession((state) => state.user);

  useEffect(() => {
    if (user !== null || username === undefined) return;

    try {
      window.localStorage.setItem(REFERRAL_KEY, username);
    } catch {
      /*
       * Private windows and blocked site data. The query string below still
       * carries it for anybody who registers in this visit, which is most
       * people who follow a link like this.
       */
    }
  }, [user, username]);

  if (username === undefined) return <Navigate to="/" replace />;

  // A member following a friend's link wants the friend, not the form.
  if (user !== null) return <Navigate to={`/${username}`} replace />;

  return <Navigate to={`/register?ref=${encodeURIComponent(username)}`} replace />;
}

/** Where a pending referral waits. Read once, at registration. */
export const REFERRAL_KEY = 'gv:ref';

/**
 * Who invited this visitor, if anybody.
 *
 * The URL wins over what is stored: somebody who has just followed a fresh link
 * is being referred by whoever sent that one, whatever was in the browser from
 * a month ago.
 */
export function pendingReferral(search: string): string | null {
  const fromUrl = new URLSearchParams(search).get('ref');

  if (fromUrl !== null && fromUrl.trim() !== '') return fromUrl.trim();

  try {
    return window.localStorage.getItem(REFERRAL_KEY);
  } catch {
    return null;
  }
}

/** Forget it, once it has been sent with a registration. */
export function clearReferral(): void {
  try {
    window.localStorage.removeItem(REFERRAL_KEY);
  } catch {
    // Nothing to do and nothing worth saying.
  }
}
